import React, { useState, useEffect } from 'react';
import {
    Button,
    Col,
    ConfigProvider,
    Form,
    Input,
    Modal,
    Row,
    Upload,
    Switch,
    Slider,
    Tooltip,
    Popconfirm
} from 'antd';
import { BiCheck, BiX, BiTrash } from "react-icons/bi";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import locale from "antd/es/locale/es_ES";
import HomeColors from "../../../../styles/HomeColors";
import { PostAula } from "../../../../dao/PostAula";
import { PostAulaIdProjecte } from "../../../../dao/PostAulaIdProjecte";
import { useParams } from "react-router-dom";
import { PostAulaIdProjecteIdTasca } from "../../../../dao/PostAulaIdProjecteIdTasca";
import { DeleteAulaIdProjecteIdTasca } from "../../../../dao/DeleteAulaIdProjecteIdTasca";
import ReactQuill from "react-quill";

interface Props {
    element: any;
    visible: boolean;
    onClose: (updated: boolean, data?: any) => void;
    alumnes: [];
    selectedIds: [];
    id_projecte?: string | number;
    id_aula?: string | number;
}

const TascaModal: React.FC<Props> = ({ element, visible, onClose, alumnes, id_projecte: props_id_projecte, id_aula: props_id_aula }) => {
    const [form] = Form.useForm();
    const [preview, setPreview] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [iaEnabled, setIaEnabled] = useState<boolean>(false);
    const params = useParams();
    const id_aula = props_id_aula || params.id_aula;
    const id_projecte = props_id_projecte || params.id_projecte;
    const [showHtml, setShowHtml] = useState(false);
    const [editorContent, setEditorContent] = useState("");

    const normalizeImg = (img?: string | null) => {
        if (!img) return null;
        if (img.startsWith("base64:")) {
            const parts = img.split(":");
            img = parts[parts.length - 1];
        }
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        return `data:image/png;base64,${img}`;
    };

    useEffect(() => {
        if (element) {
            form.setFieldsValue(element);
            setPreview(normalizeImg(element.img));
            setIaEnabled(element.ia || false);
            setEditorContent(element.enunciat || "");
        } else {
            form.resetFields();
            setPreview(null);
            setIaEnabled(false);
            setEditorContent("");
        }
    }, [element, form]);

    // Update form field when editor content changes to pass validation
    useEffect(() => {
        form.setFieldsValue({ enunciat: editorContent });
    }, [editorContent, form]);

    const handleCancel = () => {
        onClose(false);
        setPreview(null);
    };

    async function handleSubmit() {
        setLoading(true);
        try {
            const values = await form.validateFields();
            const imagenBase64 = preview ? preview.split(',')[1] : null;
            // Ensure enunciat is taken from editorContent
            const payload = { ...values, img: imagenBase64, enunciat: editorContent };
            await PostAulaIdProjecteIdTasca(id_aula + "", id_projecte + "", payload);
            // Always refresh (true) on save, whether create or update
            onClose(true, payload);
        } catch (e) {
            console.error("Validation failed or error saving:", e);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete() {
        setLoading(true);
        try {
            await DeleteAulaIdProjecteIdTasca(id_aula + "", id_projecte + "", element.id);
            onClose(true, { deleted: true });
        } catch (e) {
            console.error("Error deleting task:", e);
        } finally {
            setLoading(false);
        }
    }

    // Custom toolbar for Quill if needed, otherwise default is fine.
    // We will wrap buttons in a nicer Layout

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={<div style={{ fontSize: '1.2rem', color: HomeColors.colors.color4 }}>Crear / Editar Tasca</div>}
                open={visible}
                onCancel={handleCancel}
                footer={null} // We use custom footer inside form or just custom buttons
                width={800}
                centered
                maskClosable={false}
            >
                <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark="optional">

                    {/* --- Basic Info Section --- */}
                    <div style={{ padding: '10px 0' }}>
                        <Row gutter={16}>
                            <Col span={6}>
                                <Form.Item label="ID" name="id">
                                    <Input disabled style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                </Form.Item>
                            </Col>
                            <Col span={18}>
                                <Form.Item label="Nom de la Tasca" name="nom" rules={[{ required: true, message: 'El nom és obligatori' }]}>
                                    <Input placeholder="Ex: Tasca 1: Introducció" size="large" />
                                </Form.Item>
                            </Col>
                        </Row>

                    </div>

                    {/* --- Enunciat Section --- */}
                    <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', border: '1px solid #eee', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: '1rem' }}>📄 Enunciat</span>
                            <Button
                                size="small"
                                type={showHtml ? "primary" : "default"}
                                onClick={() => setShowHtml(!showHtml)}
                            >
                                {showHtml ? "📝 Editor Visual" : "💻 Editor HTML"}
                            </Button>
                        </div>

                        <Form.Item name="enunciat" rules={[{ required: true, message: 'L\'enunciat és obligatori' }]} style={{ marginBottom: 0 }}>
                            {/* Hidden input to hold value for form validation logic if needed, referencing editorContent via useEffect */}
                            {!showHtml ? (
                                <ReactQuill
                                    theme="snow"
                                    value={editorContent}
                                    onChange={setEditorContent}
                                    style={{ height: "200px", marginBottom: "50px", background: 'white' }}
                                />
                            ) : (
                                <Input.TextArea
                                    value={editorContent}
                                    onChange={(e) => setEditorContent(e.target.value)}
                                    style={{
                                        minHeight: "250px",
                                        fontFamily: "monospace",
                                        background: "#1e1e1e",
                                        color: "#d4d4d4",
                                        border: "1px solid #333"
                                    }}
                                />
                            )}
                        </Form.Item>
                    </div>

                    {/* --- Image Upload --- */}
                    <Form.Item label="Imatge de Portada" name="imagen">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                            <Upload
                                beforeUpload={() => false}
                                maxCount={1}
                                showUploadList={false}
                                onChange={(info) => {
                                    if (info.fileList.length > 0) {
                                        const file = info.fileList[0].originFileObj as File;
                                        const reader = new FileReader();
                                        reader.onloadend = () => setPreview(reader.result as string);
                                        reader.readAsDataURL(file);
                                    } else setPreview(null);
                                }}
                            >
                                <Button icon={<UploadOutlined />}>Seleccionar Imatge</Button>
                            </Upload>

                            {preview && (
                                <div style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '4px', background: 'white' }}>
                                    <img src={preview} alt="preview" style={{ maxHeight: '80px', display: 'block' }} />
                                </div>
                            )}
                        </div>
                    </Form.Item>

                    {/* --- Settings Section --- */}
                    <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                        <h4 style={{ marginBottom: '15px' }}>⚙️ Configuració</h4>
                        <Row gutter={[16, 16]}>
                            <Col span={6}>
                                <Tooltip title="Permet als estudiants demanar ajuda i resoldre dubtes automàticament amb la intel·ligència artificial.">
                                    <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', background: iaEnabled ? '#e6f7ff' : 'transparent', cursor: 'help' }}>
                                        <div style={{ marginBottom: 5 }}>🤖 IA</div>
                                        <Form.Item name="ia" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch onChange={(checked) => setIaEnabled(checked)} />
                                        </Form.Item>
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col span={6}>
                                <Tooltip title="Permet als estudiants posar-se en cua per rebre atenció directa del professorat.">
                                    <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', cursor: 'help' }}>
                                        <div style={{ marginBottom: 5 }}>🙋 Dubtes</div>
                                        <Form.Item name="dudas" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col span={6}>
                                <Tooltip title="Habilita un xat intern perquè els estudiants puguin col·laborar i parlar entre ells.">
                                    <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', cursor: 'help' }}>
                                        <div style={{ marginBottom: 5 }}>🤝 Col·laboració</div>
                                        <Form.Item name="colab" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col span={6}>
                                <Tooltip title="Permet enganxar codi o text des del porta-retalls dins de l'IDE.">
                                    <div style={{ textAlign: 'center', padding: '10px', border: '1px solid #eee', borderRadius: '8px', cursor: 'help' }}>
                                        <div style={{ marginBottom: 5 }}>📋 Copy/Paste</div>
                                        <Form.Item name="copypaste" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                    </div>
                                </Tooltip>
                            </Col>
                        </Row>

                        {/* --- AI Fine Tuning --- */}
                        {iaEnabled && (
                            <div style={{ marginTop: '20px', padding: '15px', background: '#f0f2f5', borderRadius: '8px', borderLeft: `4px solid ${HomeColors.colors.color4}` }}>
                                <Form.Item label="Context (Prompt inicial per a la IA)" name="context" rules={[]} tooltip="Opcional. Dóna instruccions a la IA sobre com ha d'actuar.">
                                    <Input.TextArea rows={3} placeholder="Ex: Ets un professor estricte. Ajuda l'alumne sense donar-li el codi directament." style={{ resize: 'none' }} />
                                </Form.Item>
                                <Row gutter={[24, 16]}>
                                    <Col span={8}>
                                        <Form.Item label="Tokens" name="tokens" style={{ marginBottom: 0 }} tooltip="Llargada de la resposta">
                                            <Slider min={100} max={800} step={50} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="Temperatura" name="temp" style={{ marginBottom: 0 }} tooltip="Creativitat (0 = rígid, 1 = boig)">
                                            <Slider min={0.0} max={1.0} step={0.1} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item label="TopP" name="topp" style={{ marginBottom: 0 }} tooltip="Diversitat de vocabulari">
                                            <Slider min={0.0} max={1.0} step={0.1} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    {/* --- Footer Buttons --- */}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "30px", borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        {element && element.id !== 0 && element.id !== "0" && (
                            <Popconfirm
                                title="Estàs segur que vols eliminar aquesta tasca?"
                                onConfirm={handleDelete}
                                okText="Sí"
                                cancelText="No"
                            >
                                <Button danger icon={<BiTrash />} size="large" style={{ marginRight: 'auto' }}>
                                    Eliminar
                                </Button>
                            </Popconfirm>
                        )}
                        <Button onClick={handleCancel} size="large">
                            Cancel·lar
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            size="large"
                            icon={<BiCheck />}
                            style={{ backgroundColor: HomeColors.colors.color4 }}
                        >
                            Guardar Canvis
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default TascaModal;
